import { CheckCircle2Icon, HourglassIcon } from 'lucide-react';

import { runOutcome, waitingOnChildrenLabel } from '@/features/tasks';
import {
  formatElapsedSince,
  formatExactTime,
  formatRelativeTime,
} from '@/shared/utils/time';

import type { RunSummary } from '../types';

/**
 * Beside the run's title: whether it is still going, and for how long or
 * since when. Both states sit here, so the reader finds the run's status in
 * one place whichever it is.
 *
 * - **Open:** "Waiting on 3 of 4 · Design review" (spec 140 US5): the run is
 *   waiting for its tasks, not running, so no spinner. How long it has
 *   waited is in the tooltip.
 * - **Closed:** the outcome in `runOutcome`'s words, so a run that completed
 *   reads "Completed" here and in the Executions tab. A run still
 *   `awaiting_children` whose parent has already left the step (a person
 *   moved it on) reads "Closed" — `runOutcome` would call it "In Progress",
 *   which it is not.
 */
export function RunStatusChip({ summary }: { summary: RunSummary }) {
  const { execution, closed } = summary;

  if (!closed) {
    const startedAt = execution.started_at ?? execution.created_at;
    return (
      <span
        className="text-muted-foreground bg-muted flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
        title={`Waiting for its tasks since ${formatExactTime(startedAt)} (${formatElapsedSince(startedAt)})`}
      >
        <HourglassIcon
          aria-hidden="true"
          className="size-3.5 shrink-0"
          strokeWidth={1.75}
        />
        {waitingOnChildrenLabel({
          open: Math.max(summary.total - summary.done, 0),
          total: summary.total,
          workflowName: summary.targetWorkflowName,
        })}
      </span>
    );
  }

  const endedAt = execution.ended_at ?? null;
  const label =
    execution.status === 'awaiting_children'
      ? 'Closed'
      : runOutcome(execution.status).label;

  return (
    <span
      className="text-muted-foreground bg-muted flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
      title={endedAt ? formatExactTime(endedAt) : undefined}
    >
      <CheckCircle2Icon
        aria-hidden="true"
        className="size-3.5 shrink-0"
        strokeWidth={1.75}
      />
      {label}
      {endedAt ? ` ${formatRelativeTime(endedAt)}` : null}
    </span>
  );
}
